import * as d3 from 'd3'

import { Chart } from '../chart'

/**
 * Class which represents a GradientDistributionChart model used in the SurveyChart vue component
 */
export class GradientDistributionChart extends Chart {
  constructor(chartId, dataset, chartType, width, height, margin, $t, site) {
    super(chartId, dataset, chartType, width, height, margin, $t)
    this.site = site
  }

  /**
   * Draw survey chart according to the retrieved dataset
   */
  draw() {
    let that = this

    //array : cat + count
    var legends = Array.from(this.dataset.items, item => {
      return this.getLegendText(item, item.percent, true)
    })

    //Creating pie according to the current dataset
    this.createBarChart()

    //Add title
    this.svg
      .append('text')
      .attr('class', 'survey-chart-title title-font')
      .attr('x', this.width / 2)
      .attr('y', 0 - this.margin.top)
      .attr('text-anchor', 'middle')
      .text(
        this.dataset.question_index
          ? this.dataset.question_index +
              '. ' +
              this.$t('survey_questions.' + this.dataset.question)
          : '',
      )

    let percent = this.dataset.percent > 100 ? 100 : this.dataset.percent

    this.svg
      .append('text')
      .attr('class', 'subtitle')
      .attr('x', this.width / 2)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .text(
        this.dataset.question_index
          ? '(' + percent + ' % ' + this.$t('survey_respondents') + ')'
          : '',
      )

    // Used colors for the legend
    var color = d3
      .scaleOrdinal()
      .domain(legends)
      .range(this.colors)

    let legendRectSize = 15
    let legendSpacing = 5
    var row = 0

    //Create pie legend
    var legend = this.svg
      .append('g')
      .on('mouseout', that.tooltip.hide)
      .selectAll('g')
      .data(color.domain())
      .enter()
      .append('g')
      .attr('class', 'legend')
      .attr('transform', function(d, i) {
        row = i % 3 == 0 ? 0 : row + 1
        var x = Math.floor(i / 3) * 150
        var y = that.height + that.margin.bottom * 2 + row * legendRectSize
        return 'translate(' + x + ',' + y + ')'
      })
      .on('mouseover', that.tooltip.show)
      .on('mouseout', that.tooltip.hide)

    //Rectangles used for legends
    legend
      .append('rect')
      .attr('width', legendRectSize)
      .attr('height', legendRectSize)
      .attr('id', function(d, i) {
        return 'legend-' + i
      })
      .style('fill', color)
      .style('stroke', color)
      .style('opacity', '1.0')
      .attr('checked', true)
      .on('click', function(d, i) {
        var isChecked = that.svg.select('#legend-' + i).attr('checked')
        isChecked = isChecked == 'true' ? 'false' : 'true'
        that.svg.select('#legend-' + i).attr('checked', isChecked)
        that.svg
          .select('#legend-' + i)
          .style(
            'opacity',
            that.svg.select('#legend-' + i).attr('checked') == 'true' ? '1.0' : '0.2',
          )

        if (isChecked == 'true') {
          that.checked.push(that.dataset.items[i].category)
        } else {
          that.checked.splice(that.checked.indexOf(that.dataset.items[i].category), 1)
          var checkedItem = that.dataset.items[i]
          let legend = that.getLegendText(checkedItem, checkedItem.count, true)
          that.svg.select('#legend-text-' + i).text(legend)
        }

        that.sortedDataset.items = that.dataset.items.filter(item => {
          return that.checked.indexOf(item.category) > -1
        })

        //We recalculate the total count to display a valid percentage for each categories
        var total = that.sortedDataset.items.reduce((sum, item) => {
          return item ? sum + item.count : sum
        }, 0)

        that.sortedDataset.items = that.sortedDataset.items.map(item => {
          var percent = Math.round((item.count / total) * 100)

          //Display the new percentage to the proper legend's category
          var legendIndex = that.dataset.items.findIndex(function(item2) {
            return item2.category === item.category
          })

          let legend = that.getLegendText(item, percent, true)
          that.svg.select('#legend-text-' + legendIndex).text(legend)

          return {
            category: item.category,
            count: item.count,
            percent: percent,
          }
        })

        //Creating pie according to the current dataset
        that.createBarChart()
      })

    //Add text to legend
    legend
      .append('text')
      .attr('id', function(d, i) {
        return 'legend-text-' + i
      })
      .attr('x', legendRectSize + legendSpacing)
      .attr('y', legendRectSize - legendSpacing)
      .style('font-size', '10px')
      .text(function(d) {
        return d
      })
  }

  /**
   * Create bar chart according to the question index of the current chart
   */
  createBarChart() {
    //Draw the pie chart according to selected categories
    this.svg.select('#barchart-' + this.chartId).remove()
    this.g = this.svg.append('g').attr('id', 'barchart-' + this.chartId)
    let that = this

    var x = d3
        .scaleBand()
        .rangeRound([0, this.width])
        .padding(0.4),
      y = d3.scaleLinear().rangeRound([this.height, 0])

    x.domain(
      this.sortedDataset.items.map(function(d) {
        if (that.dataset.answer_type == 'Choice' && that.site != 13) {
          return that.$t('survey_answers.' + d.category)
        } else {
          return d.category
        }
      }),
    )
    y.domain([
      0,
      d3.max(this.sortedDataset.items, function(d) {
        return d.percent
      }),
    ])

    this.g
      .append('g')
      .attr('class', 'chart-axis bar-chart-gradient-axis')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr('y', 0)
      .attr('x', 9)
      .attr('transform', 'rotate(60)')
      .style('text-anchor', 'start')

    this.g
      .append('g')
      .attr('class', 'chart-axis bar-chart-axis')
      .call(d3.axisLeft(y).ticks(10))
      .append('text')
      .attr('y', 6)
      .attr('dy', '0.71em')
      .attr('text-anchor', 'end')
      .attr('transform', 'translate(0, 0)')

    this.g
      .selectAll('.bar')
      .data(this.sortedDataset.items)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', function(d) {
        if (that.dataset.answer_type == 'Choice' && that.site != 13) {
          return x(that.$t('survey_answers.' + d.category))
        } else {
          return x(d.category)
        }
      })
      .attr('y', that.height)
      .attr('width', x.bandwidth())
      .attr('height', 0)
      .attr('fill', function(d) {
        var colorIndex = that.dataset.items.findIndex(function(item) {
          return item.category === d.category
        })
        return that.colors[colorIndex]
      })
      .on('mouseover', that.tooltip.show)
      .on('mouseout', that.tooltip.hide)
      .transition()
      .duration(300)
      .attr('y', function(d) {
        return y(d.percent)
      })
      .attr('height', function(d) {
        return that.height - y(d.percent)
      })

    let xLinear = d3.scaleLinear().rangeRound([0, this.width])
    xLinear.domain([0.0, 1.0])

    //Add median
    this.g
      .append('line')
      .attr('x1', xLinear(that.dataset.median))
      .attr('y1', 0)
      .attr('x2', xLinear(that.dataset.median))
      .attr('y2', that.height)
      .attr('stroke', 'red')
      .attr('stroke-width', '3px')

    //Add y axis title
    this.svg
      .append('text')
      .attr('id', 'y-axis-title')
      .attr('class', 'title')
      .attr('transform', 'rotate(0)')
      .attr('y', this.height / 2)
      .attr('x', 0 - this.margin.right * 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text('(%)')
  }

  /**
   * Set new site
   * @param {*} site
   */
  setSite(site) {
    this.site = site
  }

  /**
   * Get legend text according to item and percent value
   * @param {*} item
   * @param {*} percent
   */
  getLegendText(item, percent, limited) {
    let that = this

    let legend = ''

    if (this.dataset.answer_type == 'Choice') {
      if (
        this.dataset.question == 'Survey_Origin_Question' ||
        this.dataset.question == 'Survey_Origin_Pays' ||
        this.dataset.question == 'Survey_Origin_Departement' ||
        this.dataset.question == 'Survey_Origin_France'
      ) {
        if (String(item.category).startsWith('Survey_')) {
          legend =
            that.$t('survey_answers.' + item.category) + ' - ' + item.count + ' (' + percent + '%)'
        } else {
          legend = item.category + ' - ' + item.count + ' (' + percent + '%)'
        }
      } else if (item.category == this.$t('others')) {
        legend = item.category + ' - ' + item.count + ' (' + percent + '%)'
      } else {
        legend =
          that.$t('survey_answers.' + item.category) + ' - ' + item.count + ' (' + percent + '%)'
      }
    } else {
      legend = item.category + ' - ' + item.count + ' (' + percent + '%)'
    }

    if (limited == true && legend.length > 25 && this.dataset.items.length > 3) {
      legend = String(legend).substring(0, 25) + '...'
    }

    return legend
  }

  setColorsSet(colors) {
    this.colors = colors
  }

  /**
   *
   * @param {} otherCategories
   */
  initTooltip(otherCategories) {
    super.initTooltip(otherCategories)
  }
}
